/**
 * Home.
 *
 * The screen you land on after signing in. It answers exactly one question —
 * "which of my groups am I opening?" — and then gets out of the way. It is
 * deliberately not a dashboard: activity counters and widgets here would
 * compete with the photos themselves, which are the point of the product.
 *
 * Two affordances earn their place once the list stops fitting on a screen:
 * pinning, so the two or three groups you actually live in stay at the top,
 * and a name filter. Both are hidden until the list is long enough to need
 * them.
 */

import { useId, useMemo, useState } from "react";
import { useT } from "../../lib/i18n.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { formatBytes, usageRatio } from "@fc/core";
import type { GroupRole } from "@fc/core";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { useAuth } from "../../lib/auth.js";
import { Link } from "@tanstack/react-router";
import { Avatar } from "../../components/ui/Avatar.js";
import { Badge } from "../../components/ui/Badge.js";
import type { BadgeVariant } from "../../components/ui/Badge.js";
import { Button } from "../../components/ui/Button.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Input } from "../../components/ui/Input.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { useToast } from "../../components/ui/Toast.js";
import { Tooltip } from "../../components/ui/Tooltip.js";
import { AlbumRow } from "./AlbumRow.js";
import { HomeStats } from "./HomeStats.js";
import { CreateGroupDialog } from "../group/CreateGroupDialog.js";
import { JoinGroupDialog } from "../group/JoinGroupDialog.js";
import "./HomePage.css";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";

/**
 * One row of `groups.list`, inferred from the client rather than restated, so
 * the card can never disagree with what the server actually sends.
 */
type GroupSummary = Awaited<
  ReturnType<typeof trpcClient.groups.list.query>
>[number];

const ROLE_LABELS: Record<GroupRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
};

// Only ownership is worth a tint — it is the one role that changes what the
// settings screen will offer you.
const ROLE_VARIANTS: Record<GroupRole, BadgeVariant> = {
  owner: "accent",
  admin: "neutral",
  member: "neutral",
};

/**
 * A search box above three cards is noise: you can read five names faster than
 * you can decide to start typing. The field appears only past this many.
 */
const SEARCH_THRESHOLD = 5;

export function HomePage() {
  const t = useT();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const searchId = useId();

  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [query, setQuery] = useState("");
  const [showStats, setShowStats] = useState(false);

  const groups = useQuery(trpc.groups.list.queryOptions());

  /*
   * Albums, across every group. Home lists groups, but a group is not what
   * anybody is looking for — “Slovenia” is, and it sits three taps inside
   * one. Fetched alongside rather than after: it is a second round trip
   * either way, and waiting for the group list first would leave the row
   * empty for the whole of the first one.
   */
  const albums = useQuery(trpc.groups.albums.queryOptions());
  const listKey = trpc.groups.list.queryKey();

  const setFavorite = useMutation({
    mutationFn: (variables: { groupId: string; favorite: boolean }) =>
      trpcClient.groups.setFavorite.mutate(variables),

    // Pinning is a one-tap gesture made while scanning a list. Waiting a round
    // trip for the card to move sections would read as the button not working,
    // so the cache is edited first and reconciled afterwards.
    onMutate: async (variables) => {
      // Any list fetch already in flight would land after this write and
      // clobber it with the pre-toggle value.
      await queryClient.cancelQueries({ queryKey: listKey });

      const previous = queryClient.getQueryData<GroupSummary[]>(listKey);

      queryClient.setQueryData<GroupSummary[]>(listKey, (current) =>
        current?.map((group) =>
          group.id === variables.groupId
            ? { ...group, favorite: variables.favorite }
            : group,
        ),
      );

      return { previous };
    },

    onError: (error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData<GroupSummary[]>(listKey, context.previous);
      }
      toast(errorMessage(error), { variant: "danger" });
    },

    // The server owns the canonical order; re-reading it settles anything the
    // optimistic sort below guessed differently.
    onSettled: () => queryClient.invalidateQueries({ queryKey: listKey }),
  });

  const all = useMemo(() => groups.data ?? [], [groups.data]);
  const total = all.length;

  const trimmed = query.trim();
  const needle = foldForSearch(trimmed);

  const matches = useMemo(
    () =>
      needle.length === 0
        ? all
        : all.filter((group) => foldForSearch(group.name).includes(needle)),
    [all, needle],
  );

  const everyAlbum = useMemo(() => albums.data?.albums ?? [], [albums.data]);

  /*
   * The same query, run over albums. Somebody typing “slovenia” means the
   * place, and whether we filed it as a group or an album inside one is our
   * bookkeeping, not theirs — so one field searches both.
   */
  const albumMatches = useMemo(() => {
    if (needle.length === 0) return [];
    return everyAlbum.filter(
      (album) =>
        foldForSearch(album.name).includes(needle) ||
        foldForSearch(album.groupName).includes(needle) ||
        foldForSearch(album.locationName ?? "").includes(needle),
    );
  }, [everyAlbum, needle]);

  // `filter` copies, so sorting in place here never touches the query cache.
  // The server sorts too, but an optimistic pin has to land in the right slot
  // immediately rather than jumping when the refetch settles.
  const pinned = useMemo(
    () => matches.filter((group) => group.favorite).sort(byName),
    [matches],
  );
  const others = useMemo(
    () => matches.filter((group) => !group.favorite).sort(byName),
    [matches],
  );

  // Fixed at mount: a greeting that changes while you are looking at it is
  // unsettling, and nobody is on this screen long enough for it to go stale.
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);

  const firstName = (auth.user?.displayName ?? "").trim().split(/\s+/)[0];
  /*
   * Now that this searches albums too, the threshold has to count them: two
   * groups holding thirty albums between them is exactly the library where
   * a search box earns its place, and counting only groups hid it there.
   */
  const showSearch =
    total > SEARCH_THRESHOLD || everyAlbum.length > SEARCH_THRESHOLD;

  const openGroup = (groupId: string) => {
    void navigate({ to: "/g/$groupId", params: { groupId }, search: {} });
  };

  const togglePin = (group: GroupSummary) => {
    setFavorite.mutate({ groupId: group.id, favorite: !group.favorite });
  };

  return (
    <div className="fc-home">
      {/*
        One line: face, greeting, then the two things you can do.

        The avatar led a column of its own on the right, the greeting had a
        subtitle under it, and a full-width New group button sat below that —
        three rows before the first group. The face moves to the front, where
        a profile picture belongs and where it doubles as the way into the
        account; everything else shares its line.
      */}
      <header className="fc-home__header">
        {/*
          The face *and* the greeting, one target.

          A small round avatar is not obviously a button, and the name beside
          it is the part somebody actually recognises as theirs. Making only
          the picture pressable leaves the larger, more obvious half inert.
        */}
        <div className="fc-home__identity">
          <Link
            className="fc-home__me"
            to="/account"
            aria-label={t("Your account")}
          >
            <Avatar
              name={auth.user?.displayName ?? "?"}
              src={auth.user?.avatarImage ?? undefined}
              size="sm"
              decorative
            />
            <h1 className="fc-home__greeting">
              {t(greeting)}
              {firstName ? `, ${firstName}` : ""}
            </h1>
          </Link>

          <p className="fc-home__subtitle">
            {total > 0
              ? "Pick up where you left off."
              : "Your groups will appear here."}
          </p>
        </div>

        <div className="fc-home__actions">
          {/*
            The statistics, folded behind their own button.

            Four numbers permanently occupying the middle of the screen is a
            dashboard, and this is not one — they are worth a look now and
            then and worth nothing the rest of the time. The groups get the
            space back.
          */}
          {total > 0 && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={t("Your library")}
              aria-pressed={showStats}
              onClick={() => setShowStats((shown) => !shown)}
            >
              <StatsIcon />
            </Button>
          )}

          {total > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCreating(true)}
            >
              {t("New group")}
            </Button>
          )}
        </div>
      </header>

      {groups.isError && (
        <p className="fc-home__error" role="alert">
          {errorMessage(groups.error)}
        </p>
      )}

      {showSearch && (
        <div className="fc-home__search" role="search">
          <label className="fc-sr-only" htmlFor={searchId}>
            {t("Search your groups and albums")}
          </label>

          <div className="fc-home__search-field">
            <SearchIcon />
            <Input
              id={searchId}
              className="fc-home__search-input"
              type="search"
              value={query}
              autoComplete="off"
              spellCheck={false}
              placeholder={t("Search groups and albums")}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                // Escape clears rather than blurs: on this screen the field is
                // the only thing Escape could plausibly mean, and Safari does
                // not fire `change` for its own native clear.
                if (event.key !== "Escape" || query.length === 0) return;
                event.preventDefault();
                setQuery("");
              }}
            />

            {query.length > 0 && (
              <button
                type="button"
                className="fc-home__search-clear"
                aria-label={t("Clear search")}
                onClick={() => setQuery("")}
              >
                <CloseIcon />
              </button>
            )}
          </div>

          <p className="fc-home__search-count fc-tabular" aria-live="polite">
            {trimmed.length === 0
              ? `${total.toLocaleString()} groups · ${everyAlbum.length.toLocaleString()} albums`
              : `${(matches.length + albumMatches.length).toLocaleString()} matches`}
          </p>
        </div>
      )}

      {groups.isLoading ? (
        <HomeSkeleton />
      ) : groups.data === undefined ? null : total === 0 ? (
        <EmptyState
          icon={<GroupsIcon />}
          title={t("No groups yet")}
          description={t(
            "A group is either one you start yourself, or one a friend invites you into. If somebody has sent you a link, open it — if they only gave you a code, type it in here.",
          )}
          action={
            <>
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t("Create a group")}
              </Button>
              {/*
                This text named an access code and then offered nowhere to put
                one, which is the worst version of a missing feature: the
                product tells somebody how to get in and does not let them.
              */}
              <Button variant="secondary" onClick={() => setJoining(true)}>
                {t("Join with a code")}
              </Button>
            </>
          }
        />
      ) : (
        <div className="fc-home__scroll">
          {/*
            Albums first when searching, groups first when not.

            Somebody typing a place name wants the album; somebody who typed
            nothing is here to pick a group, and the album row is the
            shortcut past that decision rather than a replacement for it.
          */}
          {trimmed.length > 0 && albumMatches.length > 0 && (
            <AlbumRow albums={albumMatches} label={t("Albums")} compact />
          )}

          {matches.length === 0 && albumMatches.length === 0 ? (
            <p className="fc-home__no-matches">
              No group or album matches “{trimmed}”.{" "}
              <button
                type="button"
                className="fc-home__link"
                onClick={() => setQuery("")}
              >
                Clear the search
              </button>
            </p>
          ) : (
            <>
              {pinned.length > 0 && (
                <section className="fc-home__section">
                  <h2 className="fc-home__section-label">{t("Pinned")}</h2>
                  <ul className="fc-home__grid" role="list">
                    {pinned.map((group) => (
                      <GroupCard
                        key={group.id}
                        group={group}
                        onOpen={openGroup}
                        onTogglePin={togglePin}
                      />
                    ))}
                  </ul>
                </section>
              )}

              {others.length > 0 && (
                <section className="fc-home__section">
                  {/* Only worth a label when there is a pinned block above it
                      to distinguish it from. */}
                  {pinned.length > 0 && (
                    <h2 className="fc-home__section-label">
                      {t("All groups")}
                    </h2>
                  )}
                  <ul className="fc-home__grid" role="list">
                    {others.map((group) => (
                      <GroupCard
                        key={group.id}
                        group={group}
                        onOpen={openGroup}
                        onTogglePin={togglePin}
                      />
                    ))}
                  </ul>
                </section>
              )}

              {trimmed.length === 0 && showStats && (
                <HomeStats albums={everyAlbum} groups={all} />
              )}
            </>
          )}
        </div>
      )}


      {/*
        Pinned below the list rather than scrolling with it.

        It was inside the scroller, which meant the shortcut to an album
        disappeared as soon as you started looking through groups — the one
        moment you might want it. Outside, it holds its place whatever the
        list is doing, and on a phone it sits just above the tab bar where a
        thumb already is.
      */}
      {trimmed.length === 0 && everyAlbum.length > 0 && (
        <div className="fc-home__dock">
          <AlbumRow albums={everyAlbum} label={t("Recent albums")} />
        </div>
      )}

      {joining && <JoinGroupDialog onClose={() => setJoining(false)} />}

      {creating && (
        <CreateGroupDialog
          onClose={() => setCreating(false)}
          onCreated={(groupId) => {
            setCreating(false);
            openGroup(groupId);
          }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────── Card ──────────────────────────────────── */

interface GroupCardProps {
  group: GroupSummary;
  onOpen: (groupId: string) => void;
  onTogglePin: (group: GroupSummary) => void;
}

function GroupCard({ group, onOpen, onTogglePin }: GroupCardProps) {
  // `usageRatio` wants a full usage record; a group summary carries only the
  // two totals, so the breakdown is zeroed rather than re-deriving the maths
  // here and letting the two drift.
  const ratio = usageRatio({
    limitBytes: group.limitBytes,
    usedBytes: group.usedBytes,
    reservedBytes: 0,
    byClass: { originals: 0, derivatives: 0, video: 0, exports: 0 },
  });

  const pinLabel = group.favorite ? `Unpin ${group.name}` : `Pin ${group.name}`;

  return (
    <li>
      <div className="fc-home__card">
        {/* Sits under the open button's stretched hit area, so the emoji and
            the badge open the group like the rest of the card does. */}
        <span className="fc-home__emoji" aria-hidden="true">
          {group.emoji ? <EmojiIcon emoji={group.emoji} /> : "🗂"}
        </span>

        <div className="fc-home__card-top">
          <Badge className="fc-home__role" variant={ROLE_VARIANTS[group.role]}>
            {ROLE_LABELS[group.role]}
          </Badge>

          <Tooltip label={pinLabel}>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className="fc-home__pin"
              aria-label={pinLabel}
              aria-pressed={group.favorite}
              onClick={(event) => {
                // The pin is painted above the card's hit area; stopping here
                // keeps a pin from also being a navigation.
                event.stopPropagation();
                onTogglePin(group);
              }}
            >
              <StarIcon filled={group.favorite} />
            </Button>
          </Tooltip>
        </div>

        <button
          type="button"
          className="fc-home__open"
          onClick={() => onOpen(group.id)}
        >
          <span className="fc-home__name">{group.name}</span>

          <span className="fc-home__count fc-tabular">
            {group.fileCount.toLocaleString()}{" "}
            {group.fileCount === 1 ? "item" : "items"}
          </span>

          <span
            className="fc-home__gauge"
            data-level={
              ratio >= 0.95 ? "critical" : ratio >= 0.8 ? "warning" : "ok"
            }
            aria-hidden="true"
          >
            <span style={{ transform: `scaleX(${Math.min(1, ratio)})` }} />
          </span>

          <span className="fc-home__storage fc-tabular">
            {formatBytes(group.usedBytes)} of {formatBytes(group.limitBytes)}
          </span>
        </button>
      </div>
    </li>
  );
}

/* ─────────────────────────────── Helpers ────────────────────────────────── */

/**
 * Folds case and combining marks so the filter matches how people actually
 * type: "wakacje" finds "Wakacje", and "Krakow" finds "Kraków" without anyone
 * reaching for the accent key.
 */
function foldForSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase();
}

function byName(a: GroupSummary, b: GroupSummary): number {
  return a.name.localeCompare(b.name);
}

function greetingForHour(hour: number): string {
  if (hour < 5) return "Still up";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function HomeSkeleton() {
  return (
    <div className="fc-home__grid" aria-hidden="true">
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} height="152px" radius="md" />
      ))}
    </div>
  );
}

/* ──────────────────────────────── Icons ─────────────────────────────────── */

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3.75l2.6 5.28 5.83.85-4.22 4.11.996 5.8L12 17.05l-5.21 2.74.996-5.8-4.22-4.11 5.83-.85z" />
    </svg>
  );
}

/** Four bars. Deliberately not a pie: the numbers behind it are not parts. */
function StatsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M5 19V11M10 19V5M15 19v-6M20 19v-9" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      className="fc-home__search-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8L20 20" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

function GroupsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8.5A2 2 0 0 1 5 6.5h3.2l1.6 2H19a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M6 6.5V5a2 2 0 0 1 2-2h3l1.5 2" />
    </svg>
  );
}
